import Form from 'components/formElements/Form';
import FormInput from 'components/formElements/FormInput';
import FormLabel from 'components/formElements/FormLabel';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserObject } from '../../context/UserContext';
import { login } from '../../services/User';
import { AlertType } from '../alert/Alert';
import Button from '../Button';
import Password from '../formElements/Password';
import UseAlert from 'hooks/UseAlert';
import * as Sentry from '@sentry/react';

interface FormModel {
  email: string;
  password: string;
}

export default function LoginForm(): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { setUser } = useUserObject();
  const location = useLocation();
  const navigate = useNavigate();

  const { alertElement: errorAlertElement, setMessage: setErrorMessage } = UseAlert(AlertType.Warning);
  const { alertElement: successAlertElement, setMessage: setSuccessMessage } = UseAlert(AlertType.Notice);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormModel>();

  // console.log(watch('email')); // watch input value by passing the name of it

  /**
   * Function that be called when submitting the form
   */
  const onSubmit = async (submittedData: FormModel) => {
    try {
      setSubmitting(true);
      const user = await login(submittedData.email, submittedData.password);

      setErrorMessage(undefined);
      setUser(user);
      Sentry.setUser({ email: user.email, id: String(user.id) });
      reset();
      navigate('/');
    } catch (e) {
      const message = typeof e === 'string' ? e : 'Either your username and/or password is wrong, or this user does not exists.';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (location.hash === '#signup_failed_invalid_token') {
      setErrorMessage('Invalid registration link, please contact support if this is a mistake');
    } else if (location.hash === '#signup_complete') {
      setSuccessMessage('Registration complete, please login');
    } else if (location.hash === '#signup_success') {
      setSuccessMessage('Thank you for your registration. Please check your mail to confirm the registration');
    } else if (location.hash === '#forgot-password-requested') {
      setSuccessMessage('Password forgot email has been send. Please check your email with further instructions');
    } else if (location.hash === '#forgot-password-token-invalid') {
      setSuccessMessage('Forgot password link is invalid');
    } else if (location.hash === '#password-reset-success') {
      setSuccessMessage('Password successfully changed. Please login with your new password');
    }
  }, [location, setErrorMessage, setSuccessMessage]);

  return (
    <>
      {errorAlertElement}
      {successAlertElement}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-4'>
          <FormLabel htmlFor='email'>Email</FormLabel>
          <FormInput
            autoFocus={true}
            error={errors.email}
            placeholder='john@doe.com'
            type='email'
            {...register('email', { required: 'Email is required' })}
          />
        </div>

        <div className='mb-4'>
          <FormLabel htmlFor='password'>Password</FormLabel>
          <Password newPassword={false} {...register('password', { required: true })} />
          {errors.password && <p className='text-red-500 text-xs italic pt-1'>This fields is required.</p>}
        </div>

        <div className='flex justify-center my-3'>
          <Button loading={submitting} htmlType='submit' type='primary' fullWidth={true}>
            Login
          </Button>
        </div>

        <div className='flex flex-col justify-center items-center gap-2 text-gray-700 dark:text-gray-200'>
          <Link to={'/forgot'} className='text-sm hover:text-primary'>
            forgot password?
          </Link>
          <Link to={'/signup'} className='text-sm hover:text-primary'>
            don&apos;t have an account? Sign up
          </Link>
        </div>
      </Form>
    </>
  );
}
